/*
  WeeklyPlanTab.jsx — Weekly collection plan (drag-and-drop planner + list view).
  Extracted from CollectionPlanningPage.jsx (Feb 2026 refactor).
  Receives a single `ctx` prop containing every state slice + handler used
  inside the JSX. This keeps the physical file split without touching the
  underlying logic or references.
*/
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  CalendarDays, ListChecks, FileSpreadsheet, Upload, Sparkles, CheckCircle,
  History, Plus, ChevronLeft, ChevronRight, X, Circle, AlertTriangle,
  Clock, Loader2, RefreshCw, Edit2, Trash2,
} from "lucide-react";
import PaginationBar from "@/pages/collection-planning/PaginationBar";
import { fmt } from "@/pages/collection-planning/planningUtils";

export default function WeeklyPlanTab({ ctx }) {
  const {
    weeklyView, setWeeklyView,
    weekStart, weeklyPlan, hasOpenEntries,
    outstandingItems, outstandingFilter, setOutstandingFilter, loadingOutstanding, plannedBillingLineIds,
    draggingItem, setDraggingItem, draggingEntry, setDraggingEntry,
    allEntries, entriesLoading, entriesPage, setEntriesPage, entriesPageSize, setEntriesPageSize,
    setShowEntryDialog, setEntryForm, setShowPastReports,
    downloadWeeklyTemplate, handleWeeklyImport, bulkAutoPlan, closeWeek, loadPastReports,
    prevWeek, guardedNextWeek, handleDropOnDay, removeEntry, loadOutstanding,
    editEntry, deleteEntry,
  } = ctx;

  return (
    <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Weekly Collection Plan</h1>
                <div className="flex gap-2 flex-wrap items-center">
                  {/* View toggle */}
                  <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="weekly-view-toggle">
                    <button type="button" onClick={() => setWeeklyView("week")} className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 ${weeklyView === "week" ? "bg-[#0066CC] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="view-week-btn">
                      <CalendarDays className="w-3.5 h-3.5" /> Week View
                    </button>
                    <button type="button" onClick={() => setWeeklyView("list")} className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 ${weeklyView === "list" ? "bg-[#0066CC] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="view-list-btn">
                      <ListChecks className="w-3.5 h-3.5" /> List View
                    </button>
                  </div>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={downloadWeeklyTemplate} data-testid="weekly-template-btn"><FileSpreadsheet className="w-3.5 h-3.5" /> Template</Button>
                  <label><Button variant="outline" size="sm" className="gap-1.5 cursor-pointer" asChild><span><Upload className="w-3.5 h-3.5" /> Import</span></Button><input type="file" accept=".xlsx" className="hidden" onChange={handleWeeklyImport} data-testid="weekly-import-input" /></label>
                  <Button variant="outline" size="sm" onClick={bulkAutoPlan} className="gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="bulk-auto-plan-btn"><Sparkles className="w-3.5 h-3.5" /> Auto-Plan Week</Button>
                  <Button variant="outline" size="sm" onClick={closeWeek} className="gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="close-week-btn"><CheckCircle className="w-3.5 h-3.5" /> Close Week</Button>
                  <Button variant="outline" size="sm" onClick={() => { loadPastReports(); setShowPastReports(true); }} className="gap-1.5 border-slate-200 text-slate-600 hover:bg-slate-50" data-testid="past-reports-btn"><History className="w-3.5 h-3.5" /> Past Reports</Button>
                  <Button onClick={() => { setEntryForm({ project_id: "", date: weekStart, planned_amount: 0, notes: "", billing_line_id: "" }); setShowEntryDialog(true); }} className="bg-[#0066CC] hover:bg-[#0052A3] gap-2" data-testid="add-entry-btn"><Plus className="w-4 h-4" /> Add Entry</Button>
                </div>
              </div>
              {weeklyView === "week" && (
                <>
                  <div className="flex items-center gap-3 bg-white rounded-lg border p-3">
                    <Button variant="ghost" size="sm" onClick={prevWeek}><ChevronLeft className="w-4 h-4" /></Button>
                    <div className="flex-1 text-center">
                      <p className="text-sm font-semibold text-slate-800">Week of {new Date(weekStart + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })} - {weeklyPlan?.week_end ? new Date(weeklyPlan.week_end + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}</p>
                      <p className="text-[10px] mt-0.5 inline-flex items-center gap-1">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${hasOpenEntries ? "bg-amber-500 animate-pulse" : "bg-emerald-500"}`}></span>
                        <span className={`font-semibold uppercase tracking-wider ${hasOpenEntries ? "text-amber-600" : "text-emerald-600"}`}>
                          {hasOpenEntries ? "Week Open" : "Week Closed"}
                        </span>
                        {hasOpenEntries && <span className="text-slate-400">— close to advance</span>}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={guardedNextWeek}
                      disabled={hasOpenEntries}
                      title={hasOpenEntries ? "Close this week before moving forward" : "Next week"}
                      className={hasOpenEntries ? "opacity-40 cursor-not-allowed" : ""}
                      data-testid="next-week-btn"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                  {weeklyPlan && (
                    <>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white rounded-lg border p-3 text-center"><p className="text-lg font-bold text-[#0066CC]">{fmt(weeklyPlan.total_planned)}</p><p className="text-[10px] text-slate-500">Planned</p></div>
                        <div className="bg-white rounded-lg border p-3 text-center"><p className="text-lg font-bold text-emerald-600">{fmt(weeklyPlan.total_collected)}</p><p className="text-[10px] text-slate-500">Received <span className="text-[9px] text-slate-400">(from Receipts)</span></p></div>
                        <div className="bg-white rounded-lg border p-3 text-center"><p className={`text-lg font-bold ${weeklyPlan.gap > 0 ? "text-red-600" : "text-emerald-600"}`}>{fmt(Math.abs(weeklyPlan.gap))}</p><p className="text-[10px] text-slate-500">{weeklyPlan.gap > 0 ? "Gap" : "Surplus"}</p></div>
                      </div>
                      <p className="text-[11px] text-slate-500 italic px-1">Tip: actual collections are pulled from the Receipts screen. <strong>Drag any billing line from the right panel</strong> onto a day to plan a collection.</p>
                      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
                        {/* Days column */}
                        <div className="space-y-3">
                          {weeklyPlan.dates?.map(date => {
                        const entries = weeklyPlan.entries_by_date?.[date] || [];
                        const dayName = new Date(date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
                        const isToday = date === new Date().toISOString().split("T")[0];
                        const isDropTarget = !!(draggingItem || draggingEntry);
                        return (
                          <div
                            key={date}
                            onDragOver={(e) => { if (draggingItem || draggingEntry) e.preventDefault(); }}
                            onDrop={(e) => handleDropOnDay(date, e)}
                            data-testid={`day-drop-zone-${date}`}
                            className={`bg-white rounded-lg border p-3 transition-all ${isToday ? "border-[#0066CC] ring-1 ring-[#0066CC]/20" : ""} ${(draggingItem || draggingEntry) ? "border-dashed border-emerald-400 bg-emerald-50/30" : ""}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <p className={`text-xs font-semibold ${isToday ? "text-[#0066CC]" : "text-slate-600"}`}>{dayName} {isToday && <span className="ml-1 text-[10px] bg-blue-100 px-1.5 py-0.5 rounded">TODAY</span>}</p>
                              <p className="text-xs text-slate-400">{fmt(entries.reduce((s, e) => s + (e.planned_amount || 0), 0))} planned</p>
                            </div>
                            {entries.length > 0 ? entries.map(e => {
                              const collected = e.collected_from_receipts || 0;
                              const planned = e.planned_amount || 0;
                              const isFullyCollected = collected >= planned && planned > 0;
                              const isPartial = collected > 0 && collected < planned;
                              const dayDate = new Date(date + "T00:00:00");
                              const todayNoTime = new Date(); todayNoTime.setHours(0, 0, 0, 0);
                              const isPastOrToday = dayDate <= todayNoTime;
                              const needsFollowUp = !isFullyCollected && isPastOrToday && planned > 0 && !e.closed;
                              const StatusIcon = e.closed ? CheckCircle : (isFullyCollected ? CheckCircle : (isPartial ? Circle : (needsFollowUp ? AlertTriangle : Clock)));
                              const iconColor = e.closed ? "text-slate-400" : (isFullyCollected ? "text-emerald-600" : (needsFollowUp ? "text-red-500" : (isPartial ? "text-amber-600" : "text-blue-500")));
                              const rowBg = e.closed ? "bg-slate-100 opacity-60" : (needsFollowUp ? "bg-red-50 border border-red-200" : "bg-slate-50");
                              const beingMoved = draggingEntry && draggingEntry.id === e.id;
                              return (
                                <div
                                  key={e.id}
                                  draggable={!e.closed}
                                  onDragStart={() => setDraggingEntry(e)}
                                  onDragEnd={() => setDraggingEntry(null)}
                                  className={`group flex items-center justify-between gap-2 py-1.5 px-2 rounded ${rowBg} text-xs mb-1 ${!e.closed ? "cursor-grab active:cursor-grabbing" : ""} ${beingMoved ? "opacity-40" : ""}`}
                                  data-testid={`weekly-entry-${e.id}`}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <StatusIcon className={`w-3.5 h-3.5 shrink-0 ${iconColor}`} />
                                    <span className={`font-medium truncate ${e.closed ? "text-slate-500 line-through" : "text-slate-800"}`}>{e.project_name}</span>
                                    {e.billing_type && (
                                      <span className="text-[10px] font-semibold bg-blue-50 text-[#0066CC] px-1.5 py-0.5 rounded shrink-0">
                                        {e.billing_type}{e.billing_frequency ? ` · ${e.billing_frequency.replace("_", " ")}` : ""}
                                      </span>
                                    )}
                                    {e.carried_forward_from && (
                                      <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1 py-0.5 rounded shrink-0" title="Carried forward from previous week">CF</span>
                                    )}
                                    {needsFollowUp && (
                                      <span className="text-[9px] font-bold bg-red-100 text-red-700 px-1 py-0.5 rounded shrink-0 animate-pulse" title="No receipt yet — follow up!">FOLLOW-UP</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 shrink-0">
                                    <div className="text-right">
                                      <span className="font-semibold text-slate-700">{fmt(planned)}</span>
                                      <span className="text-[9px] text-slate-400 ml-1">planned</span>
                                    </div>
                                    <div className="text-right">
                                      <span className={`font-semibold ${collected > 0 ? "text-emerald-600" : (needsFollowUp ? "text-red-500" : "text-slate-300")}`}>{fmt(collected)}</span>
                                      <span className="text-[9px] text-slate-400 ml-1">received</span>
                                    </div>
                                    {!e.closed && (
                                      <button
                                        type="button"
                                        onClick={() => removeEntry(e)}
                                        className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition"
                                        title="Send back to Outstanding"
                                        data-testid={`remove-entry-${e.id}`}
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            }) : <p className={`text-[10px] italic ${isDropTarget ? "text-emerald-500" : "text-slate-300"}`}>{isDropTarget ? "Drop here to plan a collection on this day" : "No entries"}</p>}
                          </div>
                        );
                      })}
                        </div>

                        {/* Outstanding sidebar */}
                        <Card className="border-slate-200 lg:sticky lg:top-4 self-start" data-testid="outstanding-sidebar">
                          <CardContent className="p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <p className="text-xs font-bold text-slate-800 flex items-center gap-1"><ListChecks className="w-3.5 h-3.5 text-[#0066CC]" /> Outstanding Bills</p>
                                <p className="text-[10px] text-slate-400">Drag any row onto a day →</p>
                              </div>
                              <Button size="sm" variant="ghost" onClick={loadOutstanding} disabled={loadingOutstanding} className="h-7 w-7 p-0"><RefreshCw className={`w-3.5 h-3.5 ${loadingOutstanding ? "animate-spin" : ""}`} /></Button>
                            </div>
                            <Input
                              placeholder="Filter…"
                              value={outstandingFilter}
                              onChange={(e) => setOutstandingFilter(e.target.value)}
                              className="h-8 text-xs mb-2"
                              data-testid="outstanding-filter-input"
                            />
                            <div className="space-y-2 max-h-[540px] overflow-y-auto pr-1">
                              {(() => {
                                const q = outstandingFilter.toLowerCase();
                                const list = outstandingItems.filter(i =>
                                  !plannedBillingLineIds.has(i.billing_line_id) && (
                                    !q || i.project_name.toLowerCase().includes(q) || (i.type || "").toLowerCase().includes(q)
                                  )
                                );
                                // Group by week_label
                                const groups = {};
                                for (const it of list) {
                                  if (!groups[it.week_label]) groups[it.week_label] = [];
                                  groups[it.week_label].push(it);
                                }
                                const weekKeys = Object.keys(groups);
                                if (weekKeys.length === 0) {
                                  return <p className="text-[11px] text-slate-400 text-center py-6">No outstanding bills.</p>;
                                }
                                return weekKeys.map(wk => (
                                  <div key={wk}>
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-2 mb-1 px-1">{wk}</p>
                                    {groups[wk].map((it, idx) => {
                                      const isUsd = it.currency === "USD";
                                      const isBeingDragged = draggingItem && draggingItem.billing_line_id === it.billing_line_id;
                                      return (
                                        <div
                                          key={`${it.billing_line_id}-${idx}`}
                                          draggable
                                          onDragStart={() => setDraggingItem(it)}
                                          onDragEnd={() => setDraggingItem(null)}
                                          data-testid={`outstanding-item-${it.billing_line_id}`}
                                          className={`border rounded-md p-2 bg-slate-50 hover:bg-blue-50 hover:border-[#0066CC] cursor-grab active:cursor-grabbing text-[11px] transition ${isBeingDragged ? "opacity-40" : ""}`}
                                        >
                                          <div className="flex items-center justify-between gap-1 mb-0.5">
                                            <p className="font-semibold text-slate-800 truncate">{it.project_name}</p>
                                            {isUsd && <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-emerald-100 text-emerald-700">USD</span>}
                                          </div>
                                          <div className="flex items-center justify-between gap-1">
                                            <span className="text-[10px] text-slate-500 truncate">{it.type} · {it.frequency.replace("_", " ")}</span>
                                            <span className="font-bold text-[#0066CC]">{isUsd ? "$" : "₹"}{new Intl.NumberFormat(isUsd ? "en-US" : "en-IN").format(it.amount)}</span>
                                          </div>
                                          {it.pending_prior > 0 && (
                                            <p className="text-[9.5px] text-amber-600 mt-0.5">Past-due: ₹{new Intl.NumberFormat("en-IN").format(Math.round(it.pending_prior))}</p>
                                          )}
                                          <p className="text-[9.5px] text-slate-400 mt-0.5">Next bill: {it.next_bill_date}</p>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ));
                              })()}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* LIST VIEW — all weekly plan entries */}
              {weeklyView === "list" && (
                <Card data-testid="weekly-list-view">
                  <CardContent className="p-0">
                    <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-800">All Planned Entries</h3>
                        <p className="text-[11px] text-slate-500">{allEntries.length} total — click row to edit, trash to delete</p>
                      </div>
                      <p className="text-xs text-slate-500">Total planned: <strong className="text-[#0066CC]">{fmt(allEntries.reduce((s, e) => s + (e.planned_amount || 0), 0))}</strong></p>
                    </div>
                    {entriesLoading ? (
                      <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                    ) : allEntries.length === 0 ? (
                      <p className="text-center text-slate-400 py-12">No planned entries yet.</p>
                    ) : (
                      <>
                        <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
                          <table className="w-full text-xs sm:text-sm" data-testid="entries-table">
                            <thead className="bg-slate-50 sticky top-0 z-10 shadow-[0_1px_0_rgba(0,0,0,0.06)]">
                              <tr>
                                <th className="text-left p-3 font-semibold text-slate-600">Date</th>
                                <th className="text-left p-3 font-semibold text-slate-600">Project</th>
                                <th className="text-right p-3 font-semibold text-slate-600">Planned</th>
                                <th className="text-left p-3 font-semibold text-slate-600 hidden md:table-cell">Notes</th>
                                <th className="text-right p-3 font-semibold text-slate-600 w-[110px]">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const sorted = [...allEntries].sort((a, b) => b.date.localeCompare(a.date));
                                const start = (entriesPage - 1) * entriesPageSize;
                                const slice = sorted.slice(start, start + entriesPageSize);
                                return slice.map(e => (
                                  <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50">
                                    <td className="p-3 whitespace-nowrap text-slate-700">{e.date}</td>
                                    <td className="p-3 font-medium text-slate-800 max-w-[260px]">
                                      <div className="truncate">{e.project_name}</div>
                                      {e.billing_type && (
                                        <span className="text-[10px] font-semibold bg-blue-50 text-[#0066CC] px-1.5 py-0.5 rounded inline-block mt-0.5">
                                          {e.billing_type}{e.billing_frequency ? ` · ${e.billing_frequency.replace("_", " ")}` : ""}
                                        </span>
                                      )}
                                    </td>
                                    <td className="p-3 text-right font-semibold text-[#0066CC]">{fmt(e.planned_amount)}</td>
                                    <td className="p-3 text-slate-500 text-[11px] hidden md:table-cell max-w-[200px] truncate">{e.notes || "—"}</td>
                                    <td className="p-3 text-right whitespace-nowrap">
                                      <Button variant="ghost" size="sm" className="h-7" onClick={() => editEntry(e)} data-testid={`edit-entry-${e.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                                      <Button variant="ghost" size="sm" className="h-7 text-red-600" onClick={() => deleteEntry(e.id)} data-testid={`delete-entry-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                                    </td>
                                  </tr>
                                ));
                              })()}
                            </tbody>
                          </table>
                        </div>
                        <PaginationBar
                          total={allEntries.length}
                          page={entriesPage}
                          pageSize={entriesPageSize}
                          onPageChange={setEntriesPage}
                          onPageSizeChange={setEntriesPageSize}
                          testId="entries-pagination"
                        />
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

  );
}
